/**
 * Per-request overrides of mal-urban feature flags.
 *
 * Selected urban feature flags can be turned on/off for the current browser only via cookie named as the flag
 * (e.g. `parcel_tracking_timeline_v2=1`). The cookies are sent to urban in the `x-feature-flags` header
 * (format `flag_name=1,other_flag=0`) with every GraphQL request (SSR and client) and have precedence
 * over the urban feature flags config.
 */
export const URBAN_FEATURE_FLAGS_HEADER = 'x-feature-flags'

/** Cookies (= urban feature flag names) that are forwarded to urban as overrides. */
export const URBAN_FEATURE_FLAG_OVERRIDE_COOKIES: ReadonlyArray<string> = [
  'parcel_tracking_timeline_v2',
]

const TRUE_VALUES = ['1', 'true']
const FALSE_VALUES = ['0', 'false']

const normalizeValue = (value: string | undefined): '1' | '0' | null => {
  const normalized = (value ?? '').trim().toLowerCase()
  if (TRUE_VALUES.includes(normalized)) {
    return '1'
  }
  if (FALSE_VALUES.includes(normalized)) {
    return '0'
  }
  return null
}

/**
 * Builds the `x-feature-flags` header value from cookies; returns null when no override cookie is set.
 * @param cookies parsed request cookies (`$shopConfig.cookies`)
 */
export const getUrbanFeatureFlagsHeaderValue = (
  cookies: Record<string, string> | undefined | null
): string | null => {
  if (!cookies) {
    return null
  }
  const pairs = URBAN_FEATURE_FLAG_OVERRIDE_COOKIES.map((name) => {
    const value = normalizeValue(cookies[name])
    return value === null ? null : `${name}=${value}`
  }).filter((pair): pair is string => pair !== null)

  return pairs.length ? pairs.join(',') : null
}

/**
 * Headers to be merged into GraphQL (urban) request headers.
 * @param cookies parsed request cookies (`$shopConfig.cookies`)
 */
export const getUrbanFeatureFlagsHeaders = (
  cookies: Record<string, string> | undefined | null
): Record<string, string> => {
  const value = getUrbanFeatureFlagsHeaderValue(cookies)
  return value ? { [URBAN_FEATURE_FLAGS_HEADER]: value } : {}
}
